import Decimal from 'decimal.js';

import type { SettlementParams, SummaryItem } from '~/components/types/invoice-types';
import { DECIMAL_ZERO, safeDecimal } from '~/Utils/decimalUtils';
import { multiplyToCentsDecimal } from '~/Utils/toCents';

import type { LineItemRow } from './types';

/**
 * Recalculate settlement summary from current line items and backend-provided parameters.
 *
 * The backend owns which rate / deductible / flag applies (business logic).
 * This function only does arithmetic — no business decisions.
 *
 * LOCKSTEP CONTRACT: this is the backend's `calculate_settlement`
 * (server/modules/damage_assessment/internal/managers/settlement_manager.py) run in
 * decimal.js, step for step — full-precision Decimal sums with NO per-line rounding, the
 * co-insurance product quantized to cents ROUND_HALF_UP, exact subtraction. Frontend flows
 * send cent-clean amounts, so for FE-written data the subtotal equals the sum of the
 * displayed rows; backend-stored sub-cents pass through unrounded, like the backend. Any change to
 * either side's arithmetic MUST be applied to the other in the same change; the shared
 * vectors in `cms_tests/unit/damage_assessment/settlement_parity_vectors.json` are asserted
 * against both engines.
 *
 * Line item splitting is gated by `params.subtotal_includes_expenses`:
 *   - true (master / legacy Fast Cover): every covered_amount flows through the
 *     formula together — items with payable_type=NULL or 'expenses' are treated
 *     like indemnity (subject to deductible / co-insurance the same way).
 *   - false (configurable DA / new): only indemnity items go through the formula;
 *     expenses are tracked separately as a total with no deductible / co-insurance.
 *
 * Policy-limit clamping is intentionally not applied here. Per-payment limit checks
 * happen at the payment layer, not in the settlement formula.
 */
export function recalculateSummary(currentLineItems: LineItemRow[], params: SettlementParams): SummaryItem[] {
  const indemnitySubtotal = currentLineItems.reduce(
    (sum, item) =>
      params.subtotal_includes_expenses || item.payable_type === 'indemnity'
        ? sum.plus(safeDecimal(item.covered_amount))
        : sum,
    DECIMAL_ZERO
  );

  const expensesTotal = params.subtotal_includes_expenses
    ? DECIMAL_ZERO
    : currentLineItems.reduce(
        (sum, item) => (item.payable_type === 'expenses' ? sum.plus(safeDecimal(item.covered_amount)) : sum),
        DECIMAL_ZERO
      );

  // Backend: eligible = (indemnity_subtotal * coinsurance).quantize(0.01, ROUND_HALF_UP).
  // multiplyToCentsDecimal keeps the product exact before the quantize.
  const eligible = multiplyToCentsDecimal(indemnitySubtotal, safeDecimal(params.coinsurance_insurer_percentage));
  const coInsuranceAmount = indemnitySubtotal.minus(eligible);

  // Deliberately NOT quantized: deductible_amount is Numeric(19, 4) and the backend applies
  // it unrounded, so snapping to cents here would invent a divergence rather than close one.
  const deductible = Decimal.min(eligible, safeDecimal(params.deductible));
  const settlement = Decimal.max(DECIMAL_ZERO, eligible.minus(deductible));

  const items: SummaryItem[] = [
    { key: 'indemnity_subtotal', label: 'Indemnity Subtotal', value: indemnitySubtotal.toNumber(), display: true },
  ];

  if (params.has_co_insurance) {
    items.push({ key: 'co_insurance', label: 'Co-Insurance', value: coInsuranceAmount.toNumber(), display: true });
    items.push({ key: 'eligible', label: 'Eligible for Reimbursement', value: eligible.toNumber(), display: true });
  }

  if (params.has_deductible) {
    items.push({
      key: 'deductible',
      label: 'Excess',
      value: deductible.toNumber(),
      display: true,
    });
  }

  items.push({ key: 'settlement', label: 'Settlement', value: settlement.toNumber(), display: true });

  if (expensesTotal.greaterThan(DECIMAL_ZERO)) {
    items.push({ key: 'expenses_total', label: 'Expenses', value: expensesTotal.toNumber(), display: true });
  }

  return items;
}
